// Engine B (server-side SDMS Formula Engine) — client-side row assembly + result keying.
//
// Pure helpers (no React, no Univer, no network) so they can be unit-tested in isolation.
// The flow they support: merge records (+ per-parameter REC/RPD rounding + per-QCType root
// role) → SdmsCalcRow[] → POST /api/v1/analysis/sdms/compute → index the computed rows by a
// stable (SysSampleCode|Parameter|RunNo) key so the sheet write-back can look each one up.
//
// Only used for templates whose SDMS Formula Setup has runnable (Fixed) bindings; every other
// template keeps its existing Univer formula path (the caller gates on `sdmsHasFormulas`).

import type { SdmsCalcRow } from './qcBatch.api';

type Rec = Record<string, unknown>;

/** The per-parameter REC/RPD rounding settings (subset of RoundParamSettings) used to
 *  round the engine's Recovery/RPD outputs — from GET sdms/rounding-settings, keyed by param. */
export interface RoundParamLite {
  recSigFig: string | null; recCutOff: string | null; recDecimal: string | null;
  rpdSigFig: string | null; rpdCutOff: string | null; rpdDecimal: string | null;
}

/** One Testparameter's full rounding settings (the rounding-settings API row shape). */
export interface RoundParamFull extends RoundParamLite {
  sigFig: string | null; cutOff: string | null; decimalPlaces: string | null;
  rptLimit: string | null; defaultResult: string | null;
}

/** Resolves a row's Testparameter by Parameter + QC type — OG ResolveTestparameter (IS/Surrogate by
 *  parameter, else parameter + QCType on the Default component); the page's buildRoundResolver. */
export type RoundSettingsResolver = (parameter: string, qcType: string | null) => RoundParamFull | undefined;

/** Stable row key: SysSampleCode + Parameter + RunNo, upper-cased and trimmed. RunNo
 *  disambiguates replicate/dilution rows that share a sample × parameter. */
export function calcRowKey(
  sys: string | null | undefined,
  parameter: string | null | undefined,
  runNo?: string | null | undefined,
): string {
  const s = (sys ?? '').trim().toUpperCase();
  const p = (parameter ?? '').trim().toUpperCase();
  const r = (runNo ?? '').toString().trim();
  return `${s}|${p}|${r}`;
}

// A legacy flag column ("InternalStandard" bit, "uqTestSurrogateId" GUID) is truthy unless it
// is empty / "0" / "false". A GUID or "1" → true.
function truthyFlag(v: string | null): boolean {
  if (v === null) return false;
  const t = v.trim().toLowerCase();
  if (t === '' || t === 'false') return false;
  if (t === 'true') return true;
  // Legacy INT flags: InternalStandard / uqTestSurrogateId are -1 = NO, 1 = YES (the merge
  // SPs emit them as text since 2026-09-22, like DevExpress FIELD()). A blanket "non-zero is
  // truthy" read -1 as a surrogate — only a POSITIVE number is a set flag.
  const n = Number(t);
  return Number.isFinite(n) ? n > 0 : true;
}

/** Build a case-insensitive lookup over a record's keys (merge records vary in casing). */
function lowerKeys(rec: Rec): Map<string, unknown> {
  const m = new Map<string, unknown>();
  for (const k of Object.keys(rec)) m.set(k.toLowerCase(), rec[k]);
  return m;
}

/** First non-empty value among the given (case-insensitive) field names, as a trimmed string. */
function pick(lo: Map<string, unknown>, ...names: string[]): string | null {
  for (const n of names) {
    const v = lo.get(n.toLowerCase());
    if (v !== null && v !== undefined && String(v).trim() !== '') return String(v);
  }
  return null;
}

/**
 * Map merge records → SdmsCalcRow[] for the compute endpoint.
 * @param records            merge records (sdmsMergeData / sdmsOpenMergeData `.records`).
 * @param roundingByParam    upper-cased parameter name → REC/RPD rounding settings.
 * @param qcRootRoleByType   upper-cased QCType name → QCRootRole (from qcTypes()).
 */
export function assembleCalcRows(
  records: readonly Rec[],
  /** Preferred: a per-row resolver (OG FormulaEngineHost.PreloadRoundingSettings — every row's
   *  SigFig / CutOff / Decimals / RptLimit / DefaultResult and REC* / RPD* come from ITS parameter +
   *  QC type, overwriting the merge record's; a row with no Testparameter keeps the record's Result
   *  settings and gets no Rec/RPD settings). Legacy: a by-parameter map of the Rec/RPD settings only,
   *  with the Result settings read from the record (kept for the parity harness). */
  rounding: ReadonlyMap<string, RoundParamLite> | RoundSettingsResolver,
  qcRootRoleByType: ReadonlyMap<string, string>,
): SdmsCalcRow[] {
  const out: SdmsCalcRow[] = [];
  for (const rec of records) {
    const lo = lowerKeys(rec);
    const sys = pick(lo, 'SYSSamplecode', 'SysSampleCode');
    const parameter = pick(lo, 'Parameter');
    const runNo = pick(lo, 'RunNo');
    const qcType = pick(lo, 'QCType', 'RunType');
    // OG PreloadRoundingSettings runs before the engine and replaces the row's settings with its own
    // parameter + QC type's (2026-09-24: G6 had fed every row the merge record's settings — the Sample
    // row's — so TOC's MS1 rounded to "1.2" where OG and Apply Rounding give "1.20").
    const full = typeof rounding === 'function' ? rounding((parameter ?? '').trim(), qcType ?? null) : undefined;
    const rr: RoundParamLite | undefined = typeof rounding === 'function'
      ? full : rounding.get((parameter ?? '').trim().toUpperCase());
    const rootRole = qcType ? (qcRootRoleByType.get(qcType.trim().toUpperCase()) ?? null) : null;

    out.push({
      rowId: calcRowKey(sys, parameter, runNo),
      numericResult: pick(lo, 'NumericResult'),
      // The engine's "Numeric Result (Direct)" step (CalculateNumericResult — Anions 1014, UV 8058) sets
      // NumericResult FROM RawResult; with no RawResult it blanked NumericResult, so the Dup / Spike source
      // results read 0 (1014: "0" where OG saved 39.2). In OG both fields read the same sheet cell, so the
      // raw input is the row's CURRENT NumericResult (an on-edit value included), else the stored RawResult
      // ("n.a." on a CCB, which the engine turns into 0 exactly as OG). (2026-09-26)
      rawResult: pick(lo, 'NumericResult', 'RawResult'),
      result: pick(lo, 'Result'),
      rptLimit: full ? full.rptLimit : pick(lo, 'RptLimit'),
      mdl: pick(lo, 'MDL'),
      df: pick(lo, 'DF', 'Dilution'),
      loq: pick(lo, 'LOQ'),
      uql: pick(lo, 'UQL'),
      sysSampleCode: sys,
      // The engine's "earliest Analyzed Date per sample" (AnalyzedDate_MinBySample) needs every row's date;
      // it was never sent, so the step had nothing to compare and filled no row (HAA, user report 2026-10-16).
      analyzedDate: pick(lo, 'AnalyzedDate'),
      spikeAmount: pick(lo, 'SpikeAmount'),
      dupSource: pick(lo, 'DupSource'),
      spikeSource: pick(lo, 'SpikeSource'),
      parameter,
      qcType,
      qcRole: pick(lo, 'QCRole'),
      qcRootRole: rootRole,
      // The two flags travel SEPARATELY, as OG's dtsample columns UQTESTSURROGATEID / InternalStandard do.
      // (Before the 2026-09-23 engine build the ISTD bit was folded into the surrogate flag so the engine
      // skipped RPD on ISTD rows; the new engine has its own IsInternalStandardRow rule for that.)
      isSurrogate: truthyFlag(pick(lo, 'uqTestSurrogateId')),
      isInternalStandard: truthyFlag(pick(lo, 'InternalStandard')),
      currentRpd: pick(lo, 'RPD'),
      currentRecovery: pick(lo, '%Recovery'),
      currentQcStatus: pick(lo, 'QCStatus'),
      qualifier: pick(lo, 'Qualifier'),
      sigFig: full ? full.sigFig : pick(lo, 'SigFig'),
      cutOff: full ? full.cutOff : pick(lo, 'CutOff'),
      decimals: full ? full.decimalPlaces : pick(lo, 'Decimal'),
      defaultResult: full ? full.defaultResult : pick(lo, 'DefaultResult'),
      recSigFig: rr?.recSigFig ?? null, recCutOff: rr?.recCutOff ?? null, recDecimal: rr?.recDecimal ?? null,
      rpdSigFig: rr?.rpdSigFig ?? null, rpdCutOff: rr?.rpdCutOff ?? null, rpdDecimal: rr?.rpdDecimal ?? null,
      recLowerLimit: pick(lo, '%RECLLIMIT'),
      recUpperLimit: pick(lo, '%RECULIMIT'),
      rpdUpperLimit: pick(lo, '%RPDULIMIT'),
      ucl: pick(lo, 'UCL'),   // Blank QCStatus (2026-09-13 engine) fails a Method Blank at/above the UCL
    });
  }
  return out;
}

/** Index computed rows by their rowId, for O(1) write-back lookup. */
export function indexComputed(rows: readonly SdmsCalcRow[]): Map<string, SdmsCalcRow> {
  const m = new Map<string, SdmsCalcRow>();
  for (const r of rows) m.set(r.rowId, r);
  return m;
}
